import React, { useState, useEffect } from "react";
import { Card, Input, Slider, ColorPicker, Select } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import api from "../interceptors/api";
import StandardButton from '../components/StandardButton';
import Footer from "../components/Footer";
import Header from "../components/Header";

const QRCodeGenerator = () => {
  const [text, setText] = useState("https://quto.dev");
  const [qrCode, setQrCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(300);
  const [margin, setMargin] = useState(4);
  const [color, setColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const errorCorrectionLevels = [
    { label: t('qr_error_correction_low'), value: "L" },
    { label: t('qr_error_correction_medium'), value: "M" },
    { label: t('qr_error_correction_quartile'), value: "Q" },
    { label: t('qr_error_correction_high'), value: "H" }
  ];
  const [errorCorrection, setErrorCorrection] = useState("H");

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      api.get(`/qr-code-generator/share/${id}`)
        .then(res => {
          const data = res.data;
          if (data.text && data.qrCode) {
            setText(data.text);
            setQrCode(data.qrCode);
            if (data.size) setSize(data.size);
            if (data.margin) setMargin(data.margin);
            if (data.color) setColor(data.color);
            if (data.bgColor) setBgColor(data.bgColor);
            if (data.errorCorrection) setErrorCorrection(data.errorCorrection);
          }
        })
        .catch(() => {
          toast.error(t('server_error'));
        });
    }
  }, [searchParams, t]);

  const generateQRCode = async () => {
    if (!text.trim()) {
      toast.error(t('enter_text_for_qr_code'));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/qr-code-generator', {
        text,
        size,
        margin,
        color,
        bgColor,
        errorCorrection
      });
      
      if (response.data.success) {
        setQrCode(response.data.qrCode);
        toast.success(t('qr_code_generated'));
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('qr_code_generation_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!qrCode) {
      toast.error(t('nothing_to_copy'));
      return;
    }
    try {
      await navigator.clipboard.writeText(qrCode);
      toast.success(t('qr_code_copied'));
    } catch {
      toast.error(t('failed_to_copy'));
    }
  };

  const handleDownload = () => {
    if (!qrCode) {
      toast.error(t('nothing_to_download'));
      return;
    }
    
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `qrcode-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(t('qr_code_downloaded'));
  };

  const handleShare = async () => {
    if (!text || !qrCode) {
      toast.error(t('nothing_to_share'));
      return;
    }
    
    try {
      const response = await api.post('/qr-code-generator/share', {
        text,
        size,
        margin,
        color,
        bgColor,
        errorCorrection,
        qrCode
      });
      
      if (response.data.id) {
        const shareUrl = `${window.location.origin}/tools/qr-code-generator?share=${response.data.id}`;
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t('share_success'));
      }
    } catch {
      toast.error(t('share_failed'));
    }
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
  };

  const presetOptions = [
    { label: t('website_url'), value: 'https://quto.dev' },
    { label: t('email'), value: 'mailto:example@domain.com' },
    { label: t('phone'), value: 'tel:+1234567890' },
    { label: t('wifi'), value: 'WIFI:S:MyNetwork;T:WPA;P:MyPassword;;' },
    { label: t('vcard'), value: 'BEGIN:VCARD\nVERSION:3.0\nFN:John Doe\nTEL:+1234567890\nEMAIL:john@example.com\nEND:VCARD' },
    { label: t('bitcoin'), value: 'bitcoin:1BvBMSEYstWetqTFn5Au4m4GFg7xJaNVN2?amount=0.01' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-6xl mx-auto px-4 pt-20">
        <Card className="shadow-lg rounded-xl mb-8">
          <div className="p-6 space-y-6">
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('content_to_encode')}
              </label>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('quick_presets')}
                </label>
                <Select
                  className="w-full"
                  placeholder={t('select_preset')}
                  onChange={(value) => setText(value)}
                  options={presetOptions}
                />
              </div>

              <Input.TextArea
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                placeholder={t('enter_text_for_qr_code')}
                value={text}
                onChange={handleTextChange}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('size')}: {size}px
                  </label>
                  <Slider
                    min={100}
                    max={1000}
                    step={50}
                    value={size}
                    onChange={setSize}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('margin')}: {margin}
                  </label>
                  <Slider
                    min={0}
                    max={20}
                    value={margin}
                    onChange={setMargin}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('error_correction')}
                  </label>
                  <Select
                    className="w-full"
                    value={errorCorrection}
                    onChange={setErrorCorrection}
                    options={errorCorrectionLevels}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('qr_color')}
                  </label>
                  <ColorPicker
                    value={color}
                    onChange={(color) => setColor(color.toHexString())}
                    showText
                    size="large"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('background_color')}
                  </label>
                  <ColorPicker
                    value={bgColor}
                    onChange={(color) => setBgColor(color.toHexString())}
                    showText
                    size="large"
                  />
                </div>
              </div>
            </div>

            <StandardButton
              onClick={generateQRCode}
              loading={loading}
              type="secondary"
              className="w-full"
            >
              {t('generate_qr_code')}
            </StandardButton>

            {qrCode && (
              <div className="mt-6 space-y-4">
                <div className="flex flex-col items-center">
                  <img
                    src={qrCode}
                    alt="Generated QR Code"
                    className="border border-gray-300 rounded-lg"
                    style={{ width: size, height: size }}
                  />
                  
                  <div className="mt-6 flex flex-wrap gap-3 justify-center">
                    <StandardButton
                      onClick={handleDownload}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t('download_png')}
                    </StandardButton>

                    <StandardButton
                      onClick={handleCopy}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t('copy_image')}
                    </StandardButton>

                    <StandardButton
                      onClick={handleShare}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t('share_button')}
                    </StandardButton>
                  </div>

                  <div className="mt-4 text-sm text-gray-600">
                    <p>{t('qr_code_tips')}</p>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                      <li>{t('qr_tip_1')}</li>
                      <li>{t('qr_tip_2')}</li>
                      <li>{t('qr_tip_3')}</li>
                      <li>{t('qr_tip_4')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default QRCodeGenerator;